import { useMutation } from '@tanstack/react-query';
import * as z from 'zod';

import {
    GATEWAY,
    type ParkingGateways,
} from 'src/features/parkingManagement/api/ParkingGatewayAPIs/useParkingGateways';
import axios from 'src/lib/axios';
import { type MutationConfig } from 'src/lib/react-query';
import { type GeneralSmartParkError } from 'src/types';
import { formatResourcePath } from 'src/utils/regex';

const RESOURCE = '/parkings/parking-gateways/';

export type CreateParkingGatewayFormSchemaValues = z.infer<typeof createParkingGatewayFormSchema>;
export const createParkingGatewayFormSchema = z.object({
    parking: z.string(),
    gateway_type: z.nativeEnum(GATEWAY),
    name: z.string().min(1),
    address: z.string().min(1),
    longitude: z
        .string()
        .min(1)
        .refine((val) => {
            const num = Number(val);
            return !isNaN(num) && num >= -180 && num <= 180;
        }),
    latitude: z
        .string()
        .min(1)
        .refine((val) => {
            const num = Number(val);
            return !isNaN(num) && num >= -90 && num <= 90;
        }),
    max_height_cm: z.coerce.number().min(0).nullable().optional(),
    max_width_cm: z.coerce.number().min(0).nullable().optional(),
});

type MutationFnType = typeof mutationFn;
const mutationFn = ({ ...data }): Promise<ParkingGateways> => {
    return axios.post(formatResourcePath(RESOURCE), data);
};

interface UseMutationOptions extends MutationConfig<MutationFnType, GeneralSmartParkError> {}
const useMutationHook = (options: UseMutationOptions = {}) =>
    useMutation({
        mutationFn,
        ...options,
    });

export { useMutationHook as useCreateParkingGateway };
